/**
 * Geometry for DeskeyChart — connector anchors on each shape's real outline,
 * drop-target hit testing, and fitting an AI-generated diagram onto the canvas.
 * Pure functions (no Konva/DOM) so they unit-test.
 */
export const SIDES = ['top', 'right', 'bottom', 'left'];

const DIRECTION = { top: { x: 0, y: -1 }, bottom: { x: 0, y: 1 }, left: { x: -1, y: 0 }, right: { x: 1, y: 0 } };
const CIRCLE_SEGMENTS = 64;
const CURVE_STEPS = 16;
const DROP_MARGIN = 14;
const CLOUD_POINTS = 12;
const CLOUD_INNER = 0.75;

// Same construction as Konva's RegularPolygon: first vertex straight up.
function regularPolygon(cx, cy, r, sides) {
  return Array.from({ length: sides }, (_, i) => {
    const a = (i * 2 * Math.PI) / sides;
    return { x: cx + r * Math.sin(a), y: cy - r * Math.cos(a) };
  });
}

function quadratic(p0, c, p1) {
  return Array.from({ length: CURVE_STEPS }, (_, i) => {
    const t = (i + 1) / CURVE_STEPS, u = 1 - t;
    return { x: u * u * p0.x + 2 * u * t * c.x + t * t * p1.x, y: u * u * p0.y + 2 * u * t * c.y + t * t * p1.y };
  });
}

/** Outline polygon in node-local coords, matching DeskeyChart's renderer; null = the box itself. */
function outlineOf(type, w, h) {
  const cx = w / 2, cy = h / 2, r = Math.min(w, h) / 2;
  switch (type) {
    case 'circle':
    case 'connector': return regularPolygon(cx, cy, r, CIRCLE_SEGMENTS);
    case 'triangle': return regularPolygon(cx, cy, r, 3);
    case 'hexagon': return regularPolygon(cx, cy, r, 6);
    case 'data': return [{ x: w * 0.15, y: 0 }, { x: w, y: 0 }, { x: w * 0.85, y: h }, { x: 0, y: h }];
    case 'manual': return [{ x: w * 0.15, y: 0 }, { x: w, y: 0 }, { x: w, y: h }, { x: 0, y: h }];
    case 'cloud': return Array.from({ length: CLOUD_POINTS }, (_, i) => {
      const a = (i * 2 * Math.PI) / CLOUD_POINTS, k = i % 2 === 0 ? 1 : CLOUD_INNER;
      return { x: cx + cx * k * Math.cos(a), y: cy + cy * k * Math.sin(a) };
    });
    case 'document': {
      const waveStart = { x: w, y: h * 0.8 }, mid = { x: w * 0.5, y: h * 0.8 };
      return [
        { x: 0, y: 0 }, { x: w, y: 0 }, waveStart,
        ...quadratic(waveStart, { x: w * 0.75, y: h * 1.1 }, mid),
        ...quadratic(mid, { x: w * 0.25, y: h * 0.5 }, { x: 0, y: h * 0.8 }),
      ];
    }
    default: return null;
  }
}

// Farthest hit of the ray origin + t·dir (t ≥ 0) on the polygon's edges.
function rayHit(origin, dir, poly) {
  let best = null;
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i], q = poly[(i + 1) % poly.length];
    const ex = q.x - p.x, ey = q.y - p.y;
    const denom = dir.x * ey - dir.y * ex;
    if (Math.abs(denom) < 1e-9) continue;
    const t = ((p.x - origin.x) * ey - (p.y - origin.y) * ex) / denom;
    const u = ((p.x - origin.x) * dir.y - (p.y - origin.y) * dir.x) / denom;
    if (t >= 0 && u >= -1e-9 && u <= 1 + 1e-9 && (best === null || t > best)) best = t;
  }
  return best === null ? null : { x: origin.x + dir.x * best, y: origin.y + dir.y * best };
}

function boxAnchor(w, h, side) {
  if (side === 'top') return { x: w / 2, y: 0 };
  if (side === 'bottom') return { x: w / 2, y: h };
  if (side === 'left') return { x: 0, y: h / 2 };
  if (side === 'right') return { x: w, y: h / 2 };
  return { x: w / 2, y: h / 2 };
}

/** Absolute point where a connector attaches to `side` of `node` — on the drawn outline. */
export function anchorPoint(node, side) {
  const { width: w, height: h } = node;
  const poly = outlineOf(node.type, w, h);
  const local = (poly && DIRECTION[side] && rayHit({ x: w / 2, y: h / 2 }, DIRECTION[side], poly)) || boxAnchor(w, h, side);
  return { x: node.x + local.x, y: node.y + local.y };
}

/** Topmost node whose box (plus a small margin) contains `point`. */
export function nodeAt(nodes, point, { excludeId = null, margin = DROP_MARGIN } = {}) {
  for (let i = nodes.length - 1; i >= 0; i--) {
    const n = nodes[i];
    if (n.id === excludeId) continue;
    if (point.x >= n.x - margin && point.x <= n.x + n.width + margin
      && point.y >= n.y - margin && point.y <= n.y + n.height + margin) return n;
  }
  return null;
}

/** Side whose anchor is closest to `point` — where the user dropped the connector. */
export function nearestSide(node, point) {
  return SIDES.reduce((best, side) => {
    const a = anchorPoint(node, side), b = anchorPoint(node, best);
    return Math.hypot(a.x - point.x, a.y - point.y) < Math.hypot(b.x - point.x, b.y - point.y) ? side : best;
  }, SIDES[0]);
}

const LAYOUT_MARGIN = 40;
const MAX_CELL_W = 260, MAX_CELL_H = 130;
const MAX_NODE_W = 160, MAX_NODE_H = 70, MIN_NODE_H = 24;
const GAP_X = 30, GAP_Y = 24, MIN_ROW_GAP = 6;
const TERMINATOR_SCALE = 0.8;
const COMPACT_HEIGHT = 45;

function sidesBetween(a, b) {
  const dx = (b.x + b.width / 2) - (a.x + a.width / 2);
  const dy = (b.y + b.height / 2) - (a.y + a.height / 2);
  if (Math.abs(dx) > Math.abs(dy)) return dx > 0 ? ['right', 'left'] : ['left', 'right'];
  // An upward link (loop back) leaves and enters on the left, so it doesn't
  // retrace the downward flow arrows between the same column of shapes.
  return dy > 0 ? ['bottom', 'top'] : ['left', 'left'];
}

/**
 * Turn the AI's {col,row} grid into canvas nodes that always fit: cells shrink
 * to the space available (nodes get smaller rather than falling off the
 * bottom) and the diagram is centred horizontally.
 */
export function layoutAIChart(aiNodes, aiConns, { width, height, makeId, style = {} }) {
  const cols = aiNodes.map(n => n.col || 0), rows = aiNodes.map(n => n.row || 0);
  const minCol = Math.min(...cols), minRow = Math.min(...rows);
  const colCount = Math.max(...cols) - minCol + 1, rowCount = Math.max(...rows) - minRow + 1;

  const cellW = Math.min(MAX_CELL_W, (width - 2 * LAYOUT_MARGIN) / colCount);
  const cellH = Math.min(MAX_CELL_H, (height - 2 * LAYOUT_MARGIN) / rowCount);
  const nodeW = Math.min(MAX_NODE_W, cellW - GAP_X);
  const nodeH = Math.min(MAX_NODE_H, Math.max(MIN_NODE_H, cellH - GAP_Y), cellH - MIN_ROW_GAP);
  const left = (width - colCount * cellW) / 2;

  const idMap = {};
  const nodes = aiNodes.map(n => {
    const scale = n.type === 'terminator' ? TERMINATOR_SCALE : 1;
    const w = nodeW * scale, h = nodeH * scale;
    const id = makeId();
    idMap[n.id] = id;
    return {
      id, type: n.type, text: n.text || '',
      x: left + ((n.col || 0) - minCol) * cellW + (cellW - w) / 2,
      y: LAYOUT_MARGIN + ((n.row || 0) - minRow) * cellH + (cellH - h) / 2,
      width: w, height: h,
      strokeWidth: 2, fontSize: h < COMPACT_HEIGHT ? 11 : 13,
      ...style,
    };
  });

  const byId = Object.fromEntries(nodes.map(n => [n.id, n]));
  const connectors = (aiConns || []).map(c => {
    const from = byId[idMap[c.from]], to = byId[idMap[c.to]];
    if (!from || !to) return null;
    const [fromSide, toSide] = sidesBetween(from, to);
    return { id: makeId(), from: from.id, to: to.id, fromSide, toSide, label: c.label || '' };
  }).filter(Boolean);

  return { nodes, connectors };
}

/** Midpoint of the longest segment of a flat [x0,y0,x1,y1,…] route — clear of the end shapes. */
export function labelPosition(points) {
  let best = { len: -1, x: points[0], y: points[1] };
  for (let i = 0; i + 3 < points.length; i += 2) {
    const [x1, y1, x2, y2] = points.slice(i, i + 4);
    const len = Math.hypot(x2 - x1, y2 - y1);
    if (len > best.len) best = { len, x: (x1 + x2) / 2, y: (y1 + y2) / 2 };
  }
  return { x: best.x, y: best.y };
}
